/**
 * Modal — a small wrapper around the native <dialog> element.
 *
 * Usage:
 *   import { Modal } from './modal.js';
 *
 *   const modal = new Modal('#myModal', {
 *     closeOnBackdropClick: true,
 *     onOpen: () => console.log('opened'),
 *     onClose: (returnValue) => console.log('closed', returnValue),
 *   });
 *
 *   modal.open();
 *   modal.close();
 *
 * Works with any number of instances on the same page — each Modal
 * instance manages its own <dialog> element and listeners.
 */

export class Modal {
    /**
     * @param {string|HTMLDialogElement} target - CSS selector or dialog element
     * @param {Object} [options]
     * @param {boolean} [options.closeOnBackdropClick=true] - close when clicking outside the dialog box
     * @param {boolean} [options.closeOnEscape=true] - allow native Esc-to-close (dialog default)
     * @param {Function} [options.onOpen] - called after the dialog opens
     * @param {Function} [options.onClose] - called after the dialog closes, receives dialog.returnValue
     * @param {string} [options.openTriggerSelector] - selector for element(s) that open this modal
     * @param {string} [options.closeTriggerSelector] - selector for element(s) inside the modal that close it
     */
    constructor(target, options = {}) {
        this.dialog =
            typeof target === 'string' ? document.querySelector(target) : target;

        if (!this.dialog) {
            throw new Error(`Modal: could not find dialog element for "${target}"`);
        }
        if (this.dialog.tagName !== 'DIALOG') {
            throw new Error('Modal: target element must be a <dialog>');
        }

        this.options = {
            closeOnBackdropClick: true,
            closeOnEscape: true,
            onOpen: null,
            onClose: null,
            openTriggerSelector: null,
            closeTriggerSelector: '[data-modal-close]',
            ...options,
        };

        this._handleBackdropClick = this._handleBackdropClick.bind(this);
        this._handleCancel = this._handleCancel.bind(this);
        this._handleClose = this._handleClose.bind(this);
        this._handleOpenTriggerClick = this._handleOpenTriggerClick.bind(this);
        this._handleCloseTriggerClick = this._handleCloseTriggerClick.bind(this);

        this._bindEvents();
    }

    _bindEvents() {
        this.dialog.addEventListener('close', this._handleClose);

        if (this.options.closeOnBackdropClick) {
            this.dialog.addEventListener('click', this._handleBackdropClick);
        }

        if (!this.options.closeOnEscape) {
            this.dialog.addEventListener('cancel', this._handleCancel);
        }

        if (this.options.openTriggerSelector) {
            document
                .querySelectorAll(this.options.openTriggerSelector)
                .forEach((btn) =>
                    btn.addEventListener('click', this._handleOpenTriggerClick)
                );
        }

        if (this.options.closeTriggerSelector) {
            this.dialog
                .querySelectorAll(this.options.closeTriggerSelector)
                .forEach((btn) =>
                    btn.addEventListener('click', this._handleCloseTriggerClick)
                );
        }
    }

    _handleBackdropClick(e) {
        // Only the dialog element itself receives the click when the
        // backdrop is clicked — anything inside the content stops propagation
        // naturally via normal DOM bubbling from descendant elements.
        if (e.target !== this.dialog) return;

        const rect = this.dialog.getBoundingClientRect();
        const clickedOutside =
            e.clientX < rect.left ||
            e.clientX > rect.right ||
            e.clientY < rect.top ||
            e.clientY > rect.bottom;

        if (clickedOutside) this.close();
    }

    _handleCancel(e) {
        // Fired on Esc before the dialog closes — prevent it to disable Esc-to-close
        e.preventDefault();
    }

    _handleClose() {
        if (typeof this.options.onClose === 'function') {
            this.options.onClose(this.dialog.returnValue);
        }
    }

    _handleOpenTriggerClick() {
        this.open();
    }

    _handleCloseTriggerClick() {
        this.close();
    }

    /** Open the modal (with backdrop, focus trap, etc). */
    open() {
        if (this.dialog.open) return;
        this.dialog.showModal();
        if (typeof this.options.onOpen === 'function') {
            this.options.onOpen();
        }
    }

    /**
     * Close the modal.
     * @param {string} [returnValue] - optional value set as dialog.returnValue
     */
    close(returnValue) {
        if (!this.dialog.open) return;
        this.dialog.close(returnValue);
    }

    /** Remove all event listeners bound by this instance. */
    destroy() {
        this.dialog.removeEventListener('close', this._handleClose);
        this.dialog.removeEventListener('click', this._handleBackdropClick);
        this.dialog.removeEventListener('cancel', this._handleCancel);

        if (this.options.openTriggerSelector) {
            document
                .querySelectorAll(this.options.openTriggerSelector)
                .forEach((btn) =>
                    btn.removeEventListener('click', this._handleOpenTriggerClick)
                );
        }

        if (this.options.closeTriggerSelector) {
            this.dialog
                .querySelectorAll(this.options.closeTriggerSelector)
                .forEach((btn) =>
                    btn.removeEventListener('click', this._handleCloseTriggerClick)
                );
        }
    }
}
